import React, { useState, useEffect } from 'react';
import api from '../services/api';
import TextareaAutosize from 'react-textarea-autosize';

const initialSearch = {
  question_id: '',
  text: '',
  state_id: '',
  category_id: '',
  correct_choice: '',
  flagged: '',
  date_from: '',
  date_to: '',
  has_media: ''
};

function AdminQuestionManager() {
  // Filter/search state
  const [searchParams, setSearchParams] = useState(initialSearch);
  
  // Data sets
  const [states, setStates] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Results + editor state
  const [results, setResults] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [editData, setEditData] = useState(null);
  const [savedData, setSavedData] = useState(null);
  
  // Media
  const [mediaPreview, setMediaPreview] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  
  // UX state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [unsaved, setUnsaved] = useState(false);
  const [msg, setMsg] = useState('');

  // Fetch lookups once
  useEffect(() => {
    (async () => {
      try {
        const [stateRes, catRes] = await Promise.all([
          api.get('/states'),
          api.get('/categories'),
        ]);
        
        // Ensure we always have arrays
        const statesData = stateRes.data?.states || stateRes.data;
        const categoriesData = catRes.data?.categories || catRes.data;
        
        console.log('States API response:', statesData);
        console.log('Categories API response:', categoriesData);
        
        setStates(Array.isArray(statesData) ? statesData : []);
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      } catch (err) {
        console.error('Failed to load states/categories:', err);
        setError('Failed to load states/categories.');
        // Ensure we set empty arrays on error
        setStates([]);
        setCategories([]);
      }
    })();
  }, []);

  // Search functionality
  const handleSearch = async (e, { fetchAll = false } = {}) => {
    if (e) e.preventDefault();
    
    setLoading(true);
    setError('');
    setResults([]);
    setCurrentIndex(0);
    setEditData(null);
    setSavedData(null);
    setMediaPreview('');
    setMediaFile(null);
    setMsg('');

    try {
      const params = {};
      Object.entries(searchParams || {}).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) params[k] = v;
      });

      if (fetchAll) {
        params.per_page = 10000;
        params.page = 1;
        params.all = 1;
      }

      console.log('Making API request to /admin/questions with params:', params);
      const res = await api.get('/admin/questions', { params });
      console.log('API response received:', res);
      
      const list = res?.data?.results ?? res?.data?.rows ?? res?.data?.items ?? res?.data ?? [];

      setResults(Array.isArray(list) ? list : []);
      setCurrentIndex(0);

      if (Array.isArray(list) && list.length) {
        const q0 = list[0];
        const stateArr = String(q0?.state_ids ?? '')
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
          .map(Number)
          .filter(n => !Number.isNaN(n));

        const normalized = { ...q0, state_ids: stateArr };
        setEditData(normalized);
        setSavedData(normalized);
        setMediaPreview(q0?.media_url || '');
        setMediaFile(null);
        setUnsaved(false);
      }
    } catch (err) {
      console.error('API request failed:', err);
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err?.message || 'Unknown error';
      
      if (status === 404) {
        setError('API endpoint not found. Please check if the backend server is running on port 5000.');
      } else if (status === 401) {
        setError('Authentication failed. Please try refreshing the page to renew your session.');
      } else {
        setError('Search failed: ' + message);
      }
    } finally {
      setLoading(false);
    }
  };

  const selectQuestion = (index) => {
    setCurrentIndex(index);
    const q = results[index];
    const stateArr = (q.state_ids || '')
      .split(',')
      .filter(Boolean)
      .map(Number);

    setEditData({ ...q, state_ids: stateArr });
    setSavedData({ ...q, state_ids: stateArr });
    setMediaPreview(q.media_url || '');
    setMediaFile(null);
    setUnsaved(false);
    setMsg('');
  };

  const handleChange = (e) => {
    const { name, value, type, selectedOptions } = e.target;
    if (name === 'state_ids') {
      const vals = Array.from(selectedOptions, o => Number(o.value));
      setEditData(prev => ({ ...prev, state_ids: vals }));
    } else if (type === 'number') {
      setEditData(prev => ({ ...prev, [name]: Number(value) }));
    } else {
      setEditData(prev => ({ ...prev, [name]: value }));
    }
    setUnsaved(true);
  };

  const handleMediaChange = (e) => {
    const file = e.target.files[0];
    setMediaFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setMediaPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setMediaPreview('');
    }
    setUnsaved(true);
  };

  const handleUndo = () => {
    if (!savedData) return;
    setEditData({ ...savedData });
    setMediaPreview(savedData.media_url || '');
    setMediaFile(null);
    setUnsaved(false);
    setMsg('Changes reverted');
  };

  const handleSave = async () => {
    if (!editData) return;
    setSaving(true);
    setMsg('');
    setError('');

    try {
      let mediaUrl = editData.media_url;

      if (mediaFile) {
        const formData = new FormData();
        formData.append('media', mediaFile);
        const upRes = await api.post('/media/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        mediaUrl = upRes.data.media_url;
      }

      const payload = { 
        ...editData, 
        media_url: mediaUrl,
        state_ids: editData.state_ids?.join(',') || ''
      };

      await api.put(`/admin/questions/${editData.question_id}`, payload);

      setSavedData({ ...editData });
      setUnsaved(false);
      setMsg('✅ Question saved successfully!');
      setMediaFile(null);

      // Update the results array
      const updatedResults = [...results];
      updatedResults[currentIndex] = { ...editData, media_url: mediaUrl };
      setResults(updatedResults);

    } catch (err) {
      setError('Save failed: ' + (err?.response?.data?.message || err?.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ 
      display: 'grid', 
      gridTemplateColumns: '300px 1fr', 
      height: '100vh', 
      backgroundColor: '#f8f9fa' 
    }}>
      
      {/* LEFT SIDEBAR - Search & Results */}
      <div style={{ 
        padding: '16px', 
        borderRight: '1px solid #dee2e6', 
        backgroundColor: '#fff',
        overflowY: 'auto'
      }}>
        
        {/* Search Form */}
        <form onSubmit={handleSearch} style={{ marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#495057' }}>
            🔍 Search Questions
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <input
              type="text"
              placeholder="Question ID"
              value={searchParams.question_id}
              onChange={(e) => setSearchParams(prev => ({ ...prev, question_id: e.target.value }))}
              style={{ padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px' }}
            />
            
            <input
              type="text"
              placeholder="Search text..."
              value={searchParams.text}
              onChange={(e) => setSearchParams(prev => ({ ...prev, text: e.target.value }))}
              style={{ padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px' }}
            />
            
            <select
              value={searchParams.state_id}
              onChange={(e) => setSearchParams(prev => ({ ...prev, state_id: e.target.value }))}
              style={{ padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px' }}
            >
              <option value="">All States</option>
              {Array.isArray(states) ? states.map(s => (
                <option key={s.state_id} value={s.state_id}>{s.state_name}</option>
              )) : null}
            </select>
            
            <select
              value={searchParams.category_id}
              onChange={(e) => setSearchParams(prev => ({ ...prev, category_id: e.target.value }))}
              style={{ padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px' }}
            >
              <option value="">All Categories</option>
              {Array.isArray(categories) ? categories.map(c => (
                <option key={c.category_id} value={c.category_id}>{c.category_name}</option>
              )) : null}
            </select>
            
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '10px',
                backgroundColor: '#007bff',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </form>

        {/* Results List */}
        {results.length > 0 && (
          <div>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#495057' }}>
              📋 Results ({results.length})
            </h4>
            <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              {results.map((q, idx) => (
                <div
                  key={q.question_id}
                  onClick={() => selectQuestion(idx)}
                  style={{
                    padding: '8px 12px',
                    borderBottom: idx < results.length - 1 ? '1px solid #e9ecef' : 'none',
                    backgroundColor: idx === currentIndex ? '#e3f2fd' : '#fff',
                    cursor: 'pointer',
                    fontSize: '13px',
                    ':hover': { backgroundColor: '#f8f9fa' }
                  }}
                >
                  <div style={{ fontWeight: '600', color: '#495057' }}>#{q.question_id}</div>
                  <div style={{ color: '#6c757d', marginTop: '2px' }}>
                    {q.question_text?.substring(0, 60)}...
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div style={{ 
            padding: '12px', 
            backgroundColor: '#f8d7da', 
            color: '#721c24', 
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            marginTop: '16px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}
      </div>

      {/* RIGHT PANEL - Question Editor */}
      <div style={{ padding: '16px', overflowY: 'auto', backgroundColor: '#fff' }}>
        
        {editData ? (
          <>
            {/* Header */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '16px',
              borderBottom: '2px solid #dee2e6'
            }}>
              <h2 style={{ margin: 0, fontSize: '24px', color: '#495057' }}>
                ✏️ Edit Question #{editData.question_id}
              </h2>
              
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={handleUndo}
                  disabled={!unsaved}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: unsaved ? '#6c757d' : '#e9ecef',
                    color: unsaved ? '#fff' : '#6c757d',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: unsaved ? 'pointer' : 'not-allowed',
                    fontSize: '14px'
                  }}
                >
                  ↶ Undo
                </button>
                
                <button
                  onClick={handleSave}
                  disabled={saving || !unsaved}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: unsaved ? '#28a745' : '#e9ecef',
                    color: unsaved ? '#fff' : '#6c757d',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: unsaved ? 'pointer' : 'not-allowed',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  {saving ? 'Saving...' : '💾 Save'}
                </button>
              </div>
            </div>

            {msg && (
              <div style={{ 
                padding: '12px', 
                backgroundColor: msg.includes('✅') ? '#d4edda' : '#fff3cd',
                color: msg.includes('✅') ? '#155724' : '#856404',
                border: '1px solid ' + (msg.includes('✅') ? '#c3e6cb' : '#ffeaa7'),
                borderRadius: '4px',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                {msg}
              </div>
            )}

            {/* Compact Form Layout */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              
              {/* LEFT COLUMN */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                
                {/* Question Text */}
                <div>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', color: '#495057' }}>
                    📝 Question Text
                  </label>
                  <TextareaAutosize
                    name="question_text"
                    minRows={4}
                    maxRows={8}
                    style={{
                      width: '100%',
                      fontSize: '15px',
                      padding: '12px',
                      border: '2px solid #ced4da',
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                    value={editData.question_text || ''}
                    onChange={handleChange}
                  />
                </div>

                {/* Answer Choices */}
                <div>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', color: '#495057' }}>
                    📋 Answer Choices
                  </label>
                  {['A','B','C','D','E'].map((choice, idx) => (
                    <div key={choice} style={{ marginBottom: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ 
                          width: '24px', 
                          height: '24px', 
                          borderRadius: '50%', 
                          backgroundColor: editData.correct_choice === choice ? '#28a745' : '#6c757d',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          {choice}
                        </div>
                        <input
                          type="text"
                          name={`choice_${choice.toLowerCase()}`}
                          value={editData[`choice_${choice.toLowerCase()}`] || ''}
                          onChange={handleChange}
                          style={{
                            flex: 1,
                            fontSize: '14px',
                            padding: '8px 12px',
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                            backgroundColor: editData.correct_choice === choice ? '#e8f5e8' : '#fff'
                          }}
                          placeholder={`Choice ${choice}...`}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Rationale */}
                <div>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', color: '#495057' }}>
                    💡 Explanation / Rationale
                  </label>
                  <TextareaAutosize
                    name="rationale"
                    minRows={3}
                    maxRows={6}
                    style={{
                      width: '100%',
                      fontSize: '14px',
                      padding: '12px',
                      border: '1px solid #ced4da',
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontFamily: 'inherit'
                    }}
                    value={editData.rationale || ''}
                    onChange={handleChange}
                    placeholder="Explain why the correct answer is correct..."
                  />
                </div>
              </div>

              {/* RIGHT COLUMN */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                
                {/* Quick Settings */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  
                  {/* Correct Answer */}
                  <div>
                    <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', color: '#495057' }}>
                      ✅ Correct Answer
                    </label>
                    <select
                      name="correct_choice"
                      value={editData.correct_choice || 'A'}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    >
                      {['A','B','C','D','E'].map(c => (
                        <option key={c} value={c}>Choice {c}</option>
                      ))}
                    </select>
                  </div>

                  {/* Difficulty */}
                  <div>
                    <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', color: '#495057' }}>
                      📊 Difficulty
                    </label>
                    <select
                      name="difficulty"
                      value={editData.difficulty || 2}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    >
                      <option value={1}>1 - Easy</option>
                      <option value={2}>2 - Medium</option>
                      <option value={3}>3 - Hard</option>
                      <option value={4}>4 - Very Hard</option>
                      <option value={5}>5 - Expert</option>
                    </select>
                  </div>
                </div>

                {/* Category */}
                <div>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', color: '#495057' }}>
                    📂 Category
                  </label>
                  <select
                    name="category_id"
                    value={editData.category_id || ''}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select Category</option>
                    {Array.isArray(categories) ? categories.map(c => (
                      <option key={c.category_id} value={c.category_id}>
                        {c.category_name}
                      </option>
                    )) : null}
                  </select>
                </div>

                {/* States */}
                <div>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', color: '#495057' }}>
                    🗺️ States
                  </label>
                  <select
                    name="state_ids"
                    multiple
                    value={editData.state_ids || []}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      minHeight: '120px',
                      padding: '8px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    {Array.isArray(states) ? states.map(s => (
                      <option key={s.state_id} value={s.state_id}>
                        {s.state_name}
                      </option>
                    )) : null}
                  </select>
                  <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                    Hold Ctrl/Cmd to select multiple
                  </div>
                </div>

                {/* Media Upload */}
                <div>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', color: '#495057' }}>
                    🖼️ Media (Image/Audio/Video)
                  </label>
                  
                  {mediaPreview && (
                    <div style={{ marginBottom: '12px' }}>
                      <img 
                        src={mediaPreview} 
                        alt="Media preview" 
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '200px', 
                          border: '1px solid #dee2e6',
                          borderRadius: '4px'
                        }} 
                      />
                    </div>
                  )}
                  
                  <input
                    type="file"
                    accept="image/*,audio/*,video/*"
                    onChange={handleMediaChange}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                {/* Status & Metadata */}
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: '#f8f9fa', 
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  fontSize: '13px'
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '8px', color: '#495057' }}>
                    📈 Question Stats
                  </div>
                  {editData.created_at && (
                    <div style={{ marginBottom: '4px' }}>
                      <strong>Created:</strong> {new Date(editData.created_at).toLocaleDateString()}
                    </div>
                  )}
                  <div style={{ marginBottom: '4px' }}>
                    <strong>Status:</strong> 
                    <span style={{ 
                      marginLeft: '4px',
                      padding: '2px 6px',
                      borderRadius: '12px',
                      backgroundColor: editData.status === 'approved' ? '#d4edda' : '#fff3cd',
                      color: editData.status === 'approved' ? '#155724' : '#856404',
                      fontSize: '12px'
                    }}>
                      {editData.status || 'draft'}
                    </span>
                  </div>
                  <div>
                    <strong>Times Answered:</strong> {editData.times_answered_on || 0}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px',
            color: '#6c757d',
            fontSize: '16px'
          }}>
            👈 Search for questions to start editing
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminQuestionManager;